import { JSX, useCallback, useEffect, useRef } from 'react';
import { TranslationService } from '../../../../../services/translation.service';

function GroupedListWithNavigation<T extends { name: string }>({
  groupedItems,
  renderItem,
  focusableSections = false,
}: {
  groupedItems: Record<string, T[]>;
  renderItem: (item: T) => JSX.Element;
  focusableSections?: boolean;
}) {
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollToSection = useCallback(
    (char: string) => {
      const ref = sectionRefs.current[char];
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        if (focusableSections) ref.focus();
      }
    },
    [focusableSections]
  );

  function handleNavigationClick(char: string) {
    window.location.hash = char;
    scrollToSection(char);
  }

  useEffect(() => {
    setTimeout(() => {
      if (window.location.hash) {
        const hash = window.location.hash.substring(1).toUpperCase();
        scrollToSection(hash);
      }
    }, 2000);
  }, [scrollToSection]);

  const characters = Object.keys(groupedItems).sort();
  return (
    <>
      <h2>{TranslationService.get('common.navigation')}</h2>
      <nav className="mb-4">
        <ol className="list-unstyled d-flex flex-wrap gap-2">
          {characters.map((char) => (
            <li key={char}>
              <button
                className="btn btn-link p-0 text-decoration-underline"
                onClick={() => handleNavigationClick(char)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleNavigationClick(char);
                  }
                }}
                type="button"
              >
                {char}
              </button>
            </li>
          ))}
        </ol>
      </nav>
      {characters.map((key) => {
        const items = groupedItems[key];
        const sectionId = key.toUpperCase();
        return (
          <div
            key={sectionId}
            ref={(el) => {
              sectionRefs.current[sectionId] = el;
            }}
            id={sectionId}
            {...(focusableSections ? { tabIndex: -1 } : {})}
            className={key === characters[characters.length - 1] ? '' : 'mb-3'}
          >
            <h2 className="text-uppercase">{key}</h2>
            <ul className="list-unstyled last-child-margin-bottom-none">
              {items.map((item, index) => (
                <li key={index} className="mb-2">
                  {renderItem(item)}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

export default GroupedListWithNavigation;
